import React, { useRef, useCallback } from "react";
import { Card, CardContent, Typography, Box, IconButton } from "@mui/material";
import color from "../../Constants/colors";
import { useQuestionListForm } from "../../Core/useQuestionListForm";
import CertificationSelect from "./CertificationSelect";
import CategoryItem from "./CategoryItem";
import ActionButtons from "./ActionButtons";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { ComoInput } from "../Common/ComoInput";
import { ComoButton } from "../Common/ComoButton";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface SortableCategoryItemProps {
  category: {
    id: string;
    name: string;
    subtitle?: string;
    description?: string;
    questions: any[];
    extraTable?: boolean;
    extraTableProperties?: string[];
    questionCategoryKey?: string;
    order?: number;
  };
  children: React.ReactNode;
}

const SortableCategoryItem: React.FC<SortableCategoryItemProps> = ({
  category,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: category.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    position: "relative" as const,
    zIndex: isDragging ? 1000 : "auto",
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        width: "100%",
        cursor: isDragging ? "grabbing" : "grab",
        touchAction: "pan-y",
      }}
    >
      {children}
    </Box>
  );
};

const QuestionListCreator: React.FC = () => {
  const { t } = useTranslation("createQuestionList");
  const { colors: workspace } = useWorkspaceColors();
  const navigate = useNavigate();
  const actionButtonsRef = useRef<HTMLDivElement>(null);

  const {
    certifications,
    isCertificationsLoading,
    isCertificationsError,
    selectedCertification,
    setSelectedCertification,
    listName,
    setListName,
    categories,
    newQuestionTexts,
    isSubmitting,
    isLoadingDetails,
    action,
    addCategory,
    removeCategory,
    updateCategory,
    addQuestion,
    removeQuestion,
    updateQuestion,
    updateNewQuestionText,
    handleSubmit,
    updateQuestionAltText,
    addExtraFlag,
    reorderQuestions,
    reorderCategories,
    hasDirtyChanges,
    dirtyCategoryIds,
    dirtyQuestionIds,
  } = useQuestionListForm();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleCategoryDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = categories.findIndex(
        (category) => category.id === active.id,
      );

      const newIndex = categories.findIndex(
        (category) => category.id === over.id,
      );

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const reorderedCategories = arrayMove(categories, oldIndex, newIndex);
      reorderCategories(reorderedCategories);
    },
    [categories, reorderCategories],
  );

  const scrollToBottom = useCallback(() => {
    actionButtonsRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, []);

  if (isLoadingDetails) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        p: 3,
        overflowY: "auto",
        backgroundColor: color.gray50,
      }}
    >
      <IconButton
        onClick={scrollToBottom}
        sx={{
          position: "fixed",
          left: "calc(var(--sidebar-width) + 14px)",
          bottom: 16,
          backgroundColor: color.accent,
          color: color.white,
          boxShadow: 3,
          transition: "left 0.2s ease, background-color 0.2s ease",
          "&:hover": {
            backgroundColor: color.primary_dark,
            color: color.accent,
          },
          zIndex: 1000,
        }}
      >
        <KeyboardArrowDownIcon />
      </IconButton>

      <Box
        sx={{
          maxWidth: "4xl",
          mx: "auto",
          gap: 3,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Card
          sx={{
            backgroundColor: color.gray50,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            overflow: "visible",
          }}
        >
          <CardContent
            sx={{
              p: 3,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: workspace.primary_dark,
                mb: 1,
              }}
            >
              {action === "edit"
                ? t("createQuestionList.certificationCategory")
                : t("createQuestionList.selectCertificate")}
            </Typography>
            <CertificationSelect
              selectedCertification={selectedCertification}
              setSelectedCertification={setSelectedCertification}
              certifications={certifications || []}
              isLoading={isCertificationsLoading}
              isError={isCertificationsError}
              disabled={action === "edit"}
            />
          </CardContent>
        </Card>

        <Card
          sx={{
            backgroundColor: color.gray50,
            borderLeft: `4px solid ${workspace.primary_dark}`,
          }}
        >
          <CardContent
            sx={{
              p: 3,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: workspace.primary_dark,
                mb: 1,
              }}
            >
              {t("createQuestionList.questionList")}
            </Typography>

            <ComoInput
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              fullWidth
              placeholder={t("createQuestionList.questionListPlaceholder")}
            />
          </CardContent>
        </Card>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleCategoryDragEnd}
        >
          <SortableContext
            items={categories.map((category) => category.id)}
            strategy={verticalListSortingStrategy}
          >
            {categories.map((category) => (
              <SortableCategoryItem key={category.id} category={category}>
                <CategoryItem
                  category={category}
                  newQuestionText={newQuestionTexts[category.id] || ""}
                  onRemoveCategory={removeCategory}
                  onUpdateCategory={updateCategory}
                  onAddQuestion={(categoryId, questionText) =>
                    addQuestion(categoryId, questionText)
                  }
                  onRemoveQuestion={removeQuestion}
                  onUpdateQuestion={updateQuestion}
                  onUpdateQuestionAltText={updateQuestionAltText}
                  onUpdateNewQuestionText={updateNewQuestionText}
                  onExtraTableFlag={addExtraFlag}
                  onReorderQuestions={reorderQuestions}
                  isDirty={
                    action === "edit" && dirtyCategoryIds.has(category.id)
                  }
                  dirtyQuestionIds={dirtyQuestionIds}
                  action={action}
                />
              </SortableCategoryItem>
            ))}
          </SortableContext>
        </DndContext>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <ComoButton onClick={addCategory}>
            {t("createQuestionList.addNewCategory")}
          </ComoButton>
        </Box>

        <ActionButtons
          ref={actionButtonsRef}
          isSubmitting={isSubmitting}
          isValid={
            !!selectedCertification &&
            (action === "edit" ? hasDirtyChanges : categories.length > 0)
          }
          action={action}
          onSubmit={handleSubmit}
          onCancel={() => navigate(-1)}
        />
      </Box>
    </Box>
  );
};

export default QuestionListCreator;
